'use client'

import * as React from 'react'
import { Bug, Check, Copy, ExternalLink, LogOut, RefreshCcw } from 'lucide-react'

import type { AuthClient } from '@package/auth/client'
import type { Locale } from '@helper/utils/types'
import { Button } from '@ui/web/components'
import { safeStringify } from '@helper/utils/data'
import { buildUrl } from '@helper/utils/format'
import { useHandleCopy } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

export interface ErrorProps {
  error: Error & { digest?: string }
  onErrorCapture?: (error: Error) => void
  reset: () => void
  className?: string
  locale: Locale
  /** We use AuthClient as props since the error page might be rendered outside of the AuthClientProvider */
  auth?: AuthClient
}

export const errorText = {
  id: {
    heading: 'Maaf, terjadi kesalahan di sistem kami',
    sub_heading: 'Ini seharusnya tidak terjadi. Kami sudah melaporkan ke tim PiBo atas hal ini.',
    button_primary: 'Muat ulang laman ini',
    question: 'Apakah kesalahan ini sering terjadi?',
    paragraph: 'Mungkin kami sedang mengalami gangguan di server. Kamu bisa cek status server PiBo.',
    button_secondary: 'Status server PiBo',
    error_button: 'Salin detil error',
    logout_button: 'Keluar',
  },

  en: {
    heading: 'Sorry, something went wrong on our system',
    sub_heading: 'This should not have happened. We have reported to the PiBo team for this.',
    button_primary: 'Reload this page',
    question: 'Does this error occur frequently?',
    paragraph: 'We might be experiencing issues with our server. You can check the PiBo server status.',
    button_secondary: 'PiBo server status',
    error_button: 'Copy error details',
    logout_button: 'Log out',
  },
} as const

export function Error({ error, onErrorCapture, reset, className, locale, auth }: ErrorProps) {
  const { copy, handleCopy } = useHandleCopy()

  React.useEffect(() => {
    onErrorCapture?.(error)
  }, [error])

  const text = errorText[locale]

  return (
    <section
      className={cn('srd:h-full srd:w-full srd:flex srd:items-center srd:justify-center srd:select-none', className)}
    >
      <section className='srd:w-full srd:h-fit srd:relative srd:max-w-[400px] srd:px-2'>
        <div className='srd:bg-main-blue-100 srd:text-neutrals-white srd:rounded-full srd:w-fit srd:h-fit srd:p-2 srd:absolute srd:top-0 srd:left-0 srd:bottom-0 srd:right-0 srd:mx-auto srd:shadow-md'>
          <Bug className='srd:w-8 srd:h-8' />
        </div>

        <section className='srd:bg-neutrals-white srd:border srd:border-main-blue-20 srd:shadow-xl srd:rounded-xl srd:pt-10 srd:mt-6 srd:px-4 srd:pb-4 srd:flex srd:flex-col srd:items-center srd:justify-center srd:text-center srd:gap-6'>
          <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-2'>
            <h1 className="srd:text-lg srd:font-semibold srd:font-['Raleway'] srd:text-main-red-100">{text.heading}</h1>

            <p>
              {text.sub_heading.replace(
                '{{status}}',
                (() => {
                  if (locale === 'id') {
                    return 'sudah melaporkan'
                  }

                  return 'have reported'
                })(),
              )}
            </p>

            <Button
              className='srd:w-fit srd:bg-main-blue-100 srd:text-neutrals-white srd:rounded-3xl'
              onClick={() => reset()}
            >
              <RefreshCcw className='srd:w-4 srd:h-4 srd:mr-2' />
              {text.button_primary}
            </Button>
          </div>

          <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-2'>
            <p>
              <strong className='srd:font-medium srd:text-main-blue-100'>{text.question}</strong> {text.paragraph}
            </p>

            <Button className='srd:w-fit srd:bg-main-blue-100 srd:text-neutrals-white srd:rounded-3xl' asChild>
              <a href='https://status.bacapibo.com' target='_blank'>
                {text.button_secondary} <ExternalLink className='srd:ml-2 srd:w-4 srd:h-4' />
              </a>
            </Button>
          </div>
        </section>

        <section className='srd:mt-4 srd:flex srd:items-center srd:justify-center srd:gap-4'>
          <Button
            className='srd:text-xs srd:px-3 srd:py-0.5 srd:rounded-2xl srd:bg-neutrals-nvar-10 srd:text-neutrals-nvar-100'
            variant='outline'
            onClick={() => handleCopy(safeStringify(error))}
          >
            {copy ? <Check className='srd:w-3 srd:h-3 srd:mr-2' /> : <Copy className='srd:w-3 srd:h-3 srd:mr-2' />}
            {text.error_button}
          </Button>

          {(() => {
            if (!auth) return null

            return (
              <Button
                className='srd:text-xs srd:px-3 srd:py-0.5 srd:rounded-2xl srd:bg-neutrals-nvar-10 srd:text-neutrals-nvar-100'
                variant='outline'
                onClick={async () => {
                  await auth.signOut({
                    fetchOptions: {
                      onSuccess: () => {
                        window.location.href = buildUrl('account', '/masuk', {
                          redirect_url: window.location.href,
                        }).href
                      },
                    },
                  })
                }}
              >
                <LogOut className='srd:w-3 srd:h-3 srd:mr-2' /> {text.logout_button}
              </Button>
            )
          })()}
        </section>
      </section>
    </section>
  )
}
